import type { NotificationCategory } from '@/domain';
import type { MyNotificationType } from '@/application/ports/PreferencesRepository';
import { useDevicePermission } from '@/application/usecases/useDeviceNotifications';
import {
  useMyNotifications,
  useSaveMyNotifications,
} from '@/application/usecases/useNotifications';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { LockIcon } from '@/ui/icons';
import { BottomSheet } from '@/ui/primitives/BottomSheet';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import { useHomeNotifications } from './useHomeNotifications';
import styles from './NotificationPreferencesSheet.module.css';

const text = strings.notifications.mine;

/**
 * A person's own notification settings. The request of 7 October 2026,
 * section 2.
 *
 * Every type their role receives, by category. Each can be turned off, unless
 * management made it mandatory, when it says so and stays on; and each can be
 * put on the home screen or left off it. Where a type appears, in the app or
 * as push, is management's to set and is shown, not offered.
 *
 * A change is kept at once, on their account, so it is the same on every
 * device. Whether THIS device may show push notifications is the one thing
 * here that is the device's own, and it is asked in the browser's own prompt.
 */
export function NotificationPreferencesSheet({ onClose }: { onClose: () => void }) {
  const mine = useMyNotifications();
  const save = useSaveMyNotifications();
  const device = useDevicePermission();
  // The whole section on their home screen, on or off: the lab's answer of 12 October 2026.
  const home = useHomeNotifications();

  const types = mine.data?.types ?? [];
  const anyPush = types.some((type) => type.push);

  /** The lists as they would be kept with one type changed. */
  function change(key: string, to: { on?: boolean; home?: boolean }) {
    const next = types.map((type) =>
      type.key !== key
        ? type
        : {
            ...type,
            on: to.on ?? type.on,
            // Turned off, it is off the home screen too; turned on, it is back on it.
            home: to.on === false ? false : (to.home ?? (to.on === true ? type.inApp : type.home)),
          },
    );
    const receivedOnHome = next.filter((type) => type.on && type.inApp);
    const onHome = receivedOnHome.filter((type) => type.home);

    save.mutate(
      {
        off: next.filter((type) => !type.on).map((type) => type.key),
        // Everything shown is what a person who never chose has: kept as that, not as a list.
        home: onHome.length === receivedOnHome.length ? null : onHome.map((type) => type.key),
      },
      { onError: (error: Error) => toast(failureWords(error), { tone: 'error' }) },
    );
  }

  return (
    <BottomSheet
      title={text.title}
      lead={text.lead}
      closeLabel={strings.notifications.close}
      onClose={onClose}
      tall
      footer={
        <Button variant="primary" block onClick={onClose}>
          {text.done}
        </Button>
      }
    >
      {/*
        Above the types, since it decides whether what they say about the home
        screen is seen at all. The same choice as the control on the section
        itself, and as the tick in Customize on a Dashboard or the Overview.
      */}
      <section className={`${styles.category} ${styles.home}`} aria-label={text.homeSection}>
        <h3>{text.homeSection}</h3>
        <ul className={styles.list}>
          <li className={styles.type}>
            <div className={styles.what}>
              <span className={styles.title}>{text.showOnHome}</span>
              <span className={styles.about}>{text.showOnHomeAbout}</span>
            </div>
            <div className={styles.choices}>
              <label>
                <input
                  type="checkbox"
                  checked={home.shown}
                  disabled={!home.ready}
                  aria-label={text.showOnHome}
                  onChange={(event) => home.set(event.target.checked)}
                />
                {text.on}
              </label>
            </div>
          </li>
        </ul>
      </section>

      {mine.isPending && <SkeletonRows rows={5} label={strings.notifications.admin.loading} />}
      {mine.isError && <p className={styles.note}>{strings.errors.server}</p>}
      {mine.data !== undefined && types.length === 0 && <p className={styles.note}>{text.none}</p>}

      {(mine.data?.categories ?? []).map((category) => (
        <Category
          key={category.key}
          label={category.label}
          types={types.filter((type) => type.category === (category.key satisfies NotificationCategory))}
          busy={save.isPending}
          onChange={change}
        />
      ))}

      {anyPush && (
        <section className={styles.device}>
          <h3>{text.device}</h3>
          <p>{text.deviceLead}</p>
          {device.permission === 'granted' && <p className={styles.allowed}>{text.deviceAllowed}</p>}
          {device.permission === 'denied' && <p className={styles.note}>{text.deviceBlocked}</p>}
          {device.permission === 'unsupported' && <p className={styles.note}>{text.deviceUnsupported}</p>}
          {device.permission === 'default' && (
            <Button onClick={() => void device.request()}>{text.deviceAllow}</Button>
          )}
        </section>
      )}
    </BottomSheet>
  );
}

function Category({
  label,
  types,
  busy,
  onChange,
}: {
  label: string;
  types: MyNotificationType[];
  busy: boolean;
  onChange: (key: string, to: { on?: boolean; home?: boolean }) => void;
}) {
  if (types.length === 0) return null;

  return (
    <section className={styles.category} aria-label={label}>
      <h3>{label}</h3>
      <ul className={styles.list}>
        {types.map((type) => (
          <li key={type.key} className={styles.type}>
            <div className={styles.what}>
              <span className={styles.title}>{type.title}</span>
              <span className={styles.about}>{type.about}</span>
              <span className={styles.where}>
                {[type.inApp ? text.inApp : null, type.push ? text.push : null]
                  .filter((each) => each !== null)
                  .join(' · ')}
              </span>
            </div>
            <div className={styles.choices}>
              {type.mandatory ? (
                <span className={styles.mandatory}>
                  <LockIcon size={12} />
                  {text.alwaysOn}
                </span>
              ) : (
                <label>
                  <input
                    type="checkbox"
                    checked={type.on}
                    disabled={busy}
                    aria-label={`${type.title}: ${text.on}`}
                    onChange={(event) => onChange(type.key, { on: event.target.checked })}
                  />
                  {text.on}
                </label>
              )}
              <label>
                <input
                  type="checkbox"
                  checked={type.home}
                  disabled={busy || !type.on || !type.inApp}
                  aria-label={`${type.title}: ${text.onHome}`}
                  onChange={(event) => onChange(type.key, { home: event.target.checked })}
                />
                {text.onHome}
              </label>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
